import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Centralised data-fetching hook.
 *
 * Encapsulates the loading + error pattern so pages don't each declare their own
 * `useCallback` loader (which previously triggered exhaustive-deps lint noise).
 * The loader is held in a ref so `reload` keeps a stable identity with no
 * dependency-array pitfalls (recommended pattern for stable references).
 *
 * @param {() => Promise<any>} loader async function returning the resource data
 * @param {{ errorMessage?: string, deps?: any[] }} options optional toast message
 *   on failure and an extra dependency array that re-runs the fetch when changed
 *   (used to refetch backend-localized data when the language toggles).
 */
export function useApiResource(loader, { errorMessage, deps = [] } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const reload = useCallback(async () => {
    try {
      const result = await loaderRef.current();
      setData(result);
      return result;
    } catch (e) {
      if (errorMessage) toast.error(errorMessage);
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [errorMessage]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, ...deps]);

  return { data, loading, reload, setData };
}
